import { BOMRecord } from './types';

/** Product type tabs on the BOM Specifications screen. */
export const BOM_PRODUCT_TYPES = ['Gable', 'Lean-To', 'Header'] as const;

/** Format a number with up to 1 decimal place (e.g. 12.5 → "12.5", 36 → "36.0"). */
export function fmtFeet(n: number): string {
  return n.toFixed(1);
}

/** Format a span range (e.g. "12.5 – 17.5"). */
export function fmtRange(from: number, to: number): string {
  return `${fmtFeet(from)} – ${fmtFeet(to)}`;
}

/** Wind speed range label (e.g. "0–115 mph"). */
export function windRangeLabel(record: Pick<BOMRecord, 'windSpeedFrom' | 'windSpeedTo'>): string {
  return `${record.windSpeedFrom}–${record.windSpeedTo} mph`;
}

/** Group label: product type and classification (e.g. "Gable · Standard", "Header"). */
export function bomGroupLabel(record: Pick<BOMRecord, 'productType' | 'classification'>): string {
  return record.classification
    ? `${record.productType} · ${record.classification}`
    : record.productType;
}

export interface BOMRecordGroup {
  key: string;
  label: string;
  windSpeedFrom: number;
  windSpeedTo: number;
  records: BOMRecord[];
}

/**
 * Groups BOM records by product type, classification and wind speed range, in the order the API returns them
 * (product type, classification, wind range, span). Records within a group are sorted by nominal span.
 */
export function groupBOMRecords(records: BOMRecord[]): BOMRecordGroup[] {
  const groups = new Map<string, BOMRecordGroup>();
  records.forEach(record => {
    const key = `${record.productType}|${record.classification}|${record.windSpeedFrom}|${record.windSpeedTo}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: bomGroupLabel(record),
        windSpeedFrom: record.windSpeedFrom,
        windSpeedTo: record.windSpeedTo,
        records: [],
      });
    }
    groups.get(key)!.records.push(record);
  });
  const result = Array.from(groups.values());
  result.forEach(group => group.records.sort((a, b) => a.nominalSpan - b.nominalSpan));
  return result;
}

export interface BOMFilter {
  riskLevel: string;
  productType: string;
  windSpeedFrom: number | 'all';
  search: string;
}

/** Filters BOM records by risk level, product type tab, wind speed range and code search. */
export function filterBOMRecords(records: BOMRecord[], filter: BOMFilter): BOMRecord[] {
  const q = filter.search.trim().toLowerCase();
  return records.filter(
    r =>
      r.riskLevel === filter.riskLevel &&
      (filter.productType === 'All' || r.productType === filter.productType) &&
      (filter.windSpeedFrom === 'all' || r.windSpeedFrom === filter.windSpeedFrom) &&
      (!q || r.trussCode.toLowerCase().includes(q))
  );
}

/** Distinct wind speed ranges present in the records, lowest first. */
export function windRanges(records: BOMRecord[]): { from: number; to: number }[] {
  const seen = new Map<number, number>();
  records.forEach(r => seen.set(r.windSpeedFrom, r.windSpeedTo));
  return Array.from(seen.entries())
    .map(([from, to]) => ({ from, to }))
    .sort((a, b) => a.from - b.from);
}

/** Risk levels present in the records ("I" first). Always includes "I". */
export function bomRiskLevels(records: BOMRecord[]): string[] {
  const levels = new Set(records.map(r => r.riskLevel));
  levels.add('I');
  return Array.from(levels).sort();
}

/** Human-readable risk label used in headers and exports. */
export function riskLabel(riskLevel: string): string {
  return riskLevel === 'II' ? 'Risk II - Permanent Dwelling' : 'Risk I';
}
